/**
 * Consumer notification inbox API client (Plan 287 M1 HTTP surface).
 *
 * Wire types mirror `backend/internal/api/consumer_notifications.go` exactly.
 * All paths are prefixed with `VITE_API_URL` via `api` (worktree relative-fetch
 * rule — relative fetch() 502s in worktrees).
 */
import { api } from "./api";

// ── Wire types ─────────────────────────────────────────────────────────────────

export type NotificationUrgency = "realtime" | "digest";
export type NotificationStateClass = "action_required" | "info";
export type NotificationReadState = "unread" | "seen" | "read";

/** The semantic type of the deep-link target. Mirrors consumerinbox.ActionKind. */
export type NotificationActionKind =
  | "offer"
  | "connection_status"
  | "offers_list"
  | "rate_watch"
  | "bank"
  | "documents"
  | "passport"
  | "relationship_profile"
  | "messages"
  | "connection_checkin";

export interface NotificationContent {
  type: "text";
  title: string;
  body: string;
}

export interface NotificationActionRef {
  kind: NotificationActionKind;
  target_id?: string;
}

/** One row from GET /api/consumer/notifications. */
export interface ConsumerNotification {
  id: string;
  type: string;
  urgency: NotificationUrgency;
  state_class: NotificationStateClass;
  content: NotificationContent;
  action?: NotificationActionRef;
  read_state: NotificationReadState;
  dedupe_key?: string;
  created_at: string;
  seen_at?: string;
  read_at?: string;
}

export interface NotificationsListResponse {
  notifications: ConsumerNotification[];
  next_cursor?: string;
}

export interface UnreadCountResponse {
  unread_count: number;
}

// ── API functions ───────────────────────────────────────────────────────────────

/** GET /api/consumer/notifications — cursor-paged, newest-first. */
export async function listNotifications(
  getToken: () => Promise<string | null>,
  opts: { cursor?: string; limit?: number } = {},
): Promise<NotificationsListResponse> {
  const token = (await getToken()) ?? undefined;
  const params = new URLSearchParams();
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  const qs = params.size > 0 ? `?${params.toString()}` : "";
  return api.get<NotificationsListResponse>(
    `/api/consumer/notifications${qs}`,
    token,
  );
}

/** GET /api/consumer/notifications/unread-count — badge count. */
export async function getUnreadCount(
  getToken: () => Promise<string | null>,
): Promise<number> {
  const token = (await getToken()) ?? undefined;
  const resp = await api.get<UnreadCountResponse>(
    "/api/consumer/notifications/unread-count",
    token,
  );
  return resp.unread_count;
}

/**
 * POST /api/consumer/notifications/seen — bulk-transitions all unread rows
 * to seen. Badge clears; rows remain in the feed.
 */
export async function markNotificationsSeen(
  getToken: () => Promise<string | null>,
): Promise<void> {
  const token = (await getToken()) ?? undefined;
  await api.post<void>("/api/consumer/notifications/seen", {}, token);
}

/**
 * POST /api/consumer/notifications/:id/read — per-row read acknowledgement.
 */
export async function markNotificationRead(
  getToken: () => Promise<string | null>,
  notificationId: string,
): Promise<void> {
  const token = (await getToken()) ?? undefined;
  await api.post<void>(
    `/api/consumer/notifications/${notificationId}/read`,
    {},
    token,
  );
}
